import { useEffect, useState } from "react";
import { Alert, Select, Space, Spin, Tabs, Typography } from "antd";
import {
  AreaChartOutlined,
  AuditOutlined,
  DropboxOutlined,
  TableOutlined,
} from "@ant-design/icons";

import { apiClient } from "../../services/api/apiClient";
import { AdminAuditDashboard } from "./AdminAuditDashboard";
import { AdminProductionPanel } from "./AdminProductionPanel";
import { AdminProvincePanel } from "./AdminProvincePanel";
import { AdminSurfacePanel } from "./AdminSurfacePanel";

const { Text } = Typography;

export interface SeasonOption {
  id: number;
  label: string;
  year: number;
}

/**
 * Wrapper del dashboard admin: una sola campaña compartida entre los
 * tres paneles (Auditorías / Producción / Superficie). Hace una
 * petición ligera al endpoint `/v1/dashboard/?scope=audits` solo para
 * poblar el selector; cada panel luego pide su propio scope.
 *
 * La campaña se identifica por `Season.id` (variedad + país + año),
 * no por año — permite distinguir "Tango 2026 — España" de "Tango
 * 2026 — Perú" y, en el futuro, de "Kinnow 2026".
 */
export const AdminDashboard = () => {
  const [availableSeasons, setAvailableSeasons] = useState<SeasonOption[]>([]);
  const [season, setSeason] = useState<number | null>(null);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    // Fetch inicial para traer available_seasons y resolver la
    // campaña activa — el backend elige la más reciente por defecto.
    const run = async () => {
      try {
        const res = await apiClient.get<{
          season: SeasonOption | null;
          available_seasons: SeasonOption[];
        }>("/v1/dashboard/?scope=audits");
        setAvailableSeasons(res.data.available_seasons ?? []);
        setSeason(res.data.season?.id ?? null);
      } catch (err: any) {
        setBootstrapError(
          err?.response?.data?.detail ||
            err?.message ||
            "No se pudo cargar la lista de campañas",
        );
      } finally {
        setBootstrapping(false);
      }
    };
    run();
  }, []);

  if (bootstrapError)
    return <Alert type="error" message={bootstrapError} showIcon />;

  return (
    <Spin spinning={bootstrapping} tip="Preparando panel…">
      <div>
        {/* Selector de campaña único arriba a la derecha. El
            PageHeader lo renderiza la página contenedora
            (/home/index.tsx), aquí solo mostramos el control. */}
        <Space
          style={{
            marginBottom: 12,
            width: "100%",
            justifyContent: "flex-end",
          }}
          align="center"
          wrap
        >
          <Text type="secondary">Campaña:</Text>
          <Select
            style={{ minWidth: 240 }}
            value={season ?? undefined}
            onChange={(v) => setSeason(v)}
            options={availableSeasons.map((s) => ({
              label: s.label,
              value: s.id,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            disabled={bootstrapping}
          />
        </Space>

        <Tabs
          defaultActiveKey="audits"
          items={[
            {
              key: "audits",
              label: (
                <span>
                  <AuditOutlined /> Auditorías
                </span>
              ),
              children: <AdminAuditDashboard season={season} />,
            },
            {
              key: "production",
              label: (
                <span>
                  <DropboxOutlined /> Producción
                </span>
              ),
              children: <AdminProductionPanel season={season} />,
            },
            {
              key: "surface",
              label: (
                <span>
                  <AreaChartOutlined /> Superficie
                </span>
              ),
              children: <AdminSurfacePanel season={season} />,
            },
            {
              key: "province",
              label: (
                <span>
                  <TableOutlined /> Por provincia
                </span>
              ),
              children: <AdminProvincePanel season={season} />,
            },
          ]}
        />
      </div>
    </Spin>
  );
};
